import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {ClaimManagerFrontend} from '../../environments/application-configurations';
import {SupportedLanguage} from '../supported-languages';
import {NamesTranslation, Translation} from '../translation';
import {Observable, Subject} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LocalizationService {
  private readonly claimManagerFrontend: ClaimManagerFrontend = environment.claimManagerFrontend;
  private readonly languageKey: string = `UI_Language_${this.claimManagerFrontend.baseUrl}`;
  private translations: Record<string, Translation> = {};
  private fetchingStatus: Record<string, boolean> = {};
  private currentSupportedLanguage: SupportedLanguage = this.claimManagerFrontend.localisationConfiguration.defaultLanguage;
  private readonly currentLanguage: Subject<SupportedLanguage> = new Subject<SupportedLanguage>();
  private readonly currentLanguage$: Observable<SupportedLanguage> = this.currentLanguage.asObservable();
  private readonly currentTranslation: Subject<NamesTranslation> = new Subject();
  private readonly currentTranslation$: Observable<NamesTranslation> = this.currentTranslation.asObservable();
  private afterLanguageLoaded: (() => void) | undefined;

  public get supportedLanguages(): SupportedLanguage[] {
    return this.claimManagerFrontend.localisationConfiguration.supportedLanguages;
  }

  public get currentSetLanguage(): SupportedLanguage {
    return this.currentSupportedLanguage;
  }

  public currentTranslationChange(): Observable<NamesTranslation> {
    return this.currentTranslation$
  }

  public constructor() {
    this.currentLanguage$.subscribe(language => {
        this.currentSupportedLanguage = language;
        localStorage.setItem(this.languageKey, JSON.stringify(language));
        console.debug(`Changed language to ${language.languageId}:`, language)
        this.fetchTranslationForLanguage(language)
          .then(() => console.debug(`Fetched translations for language ${language.languageId}`))
          .then(() => {
              if (this.afterLanguageLoaded) this.afterLanguageLoaded();
            }
          )
        ;
      }
    );
  }

  public async initializeService(): Promise<void> {
    let storedLanguagePreference = localStorage.getItem(this.languageKey);
    console.debug(`storedLanguagePreference: ${storedLanguagePreference}`)
    const storedLanguage: SupportedLanguage = JSON.parse(
      storedLanguagePreference ??
      JSON.stringify(this.claimManagerFrontend.localisationConfiguration.defaultLanguage)
    ) as SupportedLanguage;

    this.currentSupportedLanguage = storedLanguage;
    await this.fetchTranslationForLanguage(storedLanguage);
    this.currentLanguage.next(storedLanguage);
  }

  public changeLanguage(languageId: string, callback?: () => void) {
    const matchingSupportedLanguage = this.supportedLanguages.find(sl => sl.languageId === languageId);
    if (matchingSupportedLanguage) {
      this.afterLanguageLoaded = callback ?? this.afterLanguageLoaded;
      this.currentLanguage.next(matchingSupportedLanguage);
    } else {
      throw new Error(`No language found for id ${languageId}`);
    }
  }

  private async fetchTranslationForLanguage(language: SupportedLanguage): Promise<void> {
    const languageId: string = language.languageId
    if (!(this.fetchingStatus[languageId] ?? false)) {
      if (this.translations[languageId]) {
        return Promise.resolve();
      }
      try {
        let translations: any;
        let r = await fetch(this.getTranslationUrl(languageId));
        if (r.ok) {
          translations = await r.json();
        } else {
          throw new Error(r.statusText)
        }
        console.debug(`fetched translations for language ${languageId}`, translations)
        this.translations = {
          ...this.translations,
          [languageId]: translations
        };
        this.fetchingStatus[languageId] = false;
        this.currentTranslation.next({language: language, translation: translations})
      } finally {
        this.fetchingStatus[languageId] = false;
      }
    } else {
      return Promise
        .reject(`it is fetching ${languageId}`)
    }
  }

  private getTranslationUrl(languageId: string) {
    return `${this.claimManagerFrontend.baseUrl}${this.claimManagerFrontend.localisationConfiguration.translationsBasePath}/language-${languageId.toLowerCase()}.json`;
  }

  public getTranslation(key: string) {
    const translation = this.translations[this.currentSetLanguage.languageId];
    if ((translation?.hasOwnProperty(key) ?? false)) {
      return translation[key];
    } else {
      console.debug(`No translation found for key ${key} for Language ${this.currentSetLanguage.languageId}`)
      console.debug(`Translations available ${Object.keys(this.translations)}`, {translations: this.translations, translation})
      return `_${key}_`;
    }
  }
}
